import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MdAssignment,
  MdContentCopy,
  MdEmail,
  MdPeople,
  MdSend,
  MdSms,
} from "react-icons/md";
import { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import Dialog from "components/dialog";
import { Checkbox, InputField, SearchInput, Select } from "components/fields";
import Loader from "components/loader/Loader";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState } from "components/states";
import { SegmentedControl } from "components/tabs";
import { track } from "lib/mixpanel";
import notify from "lib/notify";

const DELIVERY_OPTIONS = [
  { value: "email", label: "Email", icon: MdEmail },
  { value: "sms", label: "SMS", icon: MdSms },
  { value: "both", label: "Both", icon: MdSend },
].map(({ value, label, icon: Icon }) => ({
  value,
  label: (
    <span className="flex items-center gap-1.5">
      <Icon aria-hidden="true" className="h-4 w-4" />
      {label}
    </span>
  ),
}));

const people = (count) => `${count} ${count === 1 ? "person" : "people"}`;

const SendAssessmentModal = ({ isOpen, onClose, config }) => {
  const { service, labels } = config;
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("email");
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchForms = useCallback(async () => {
    try {
      setLoadingForms(true);
      const response = await service.getIntakeForms({ isActive: true });
      if (response.success && response.data?.length > 0) {
        setForms(response.data);
        setSelectedFormId(response.data[0].id);
      } else {
        setForms([]);
        setSelectedFormId(null);
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not load assessment forms"));
    } finally {
      setLoadingForms(false);
    }
  }, [service]);

  const fetchEligibleUsers = useCallback(
    async (formId) => {
      try {
        setLoadingUsers(true);
        setSelectedUserIds(new Set());
        const response = await service.getEligibleUsers(formId);
        if (response.success) {
          setUsers(response.data || []);
        }
      } catch (error) {
        notify.error(getApiErrorMessage(error, `Could not load eligible ${labels.pluralLower}`));
      } finally {
        setLoadingUsers(false);
      }
    },
    [service, labels.pluralLower]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedUserIds(new Set());
    setSearchQuery("");
    setDeliveryMethod("email");
    fetchForms();
  }, [isOpen, fetchForms]);

  useEffect(() => {
    if (selectedFormId) {
      fetchEligibleUsers(selectedFormId);
    }
  }, [selectedFormId, fetchEligibleUsers]);

  const selectedForm = useMemo(
    () => forms.find((f) => f.id === selectedFormId) || null,
    [forms, selectedFormId]
  );

  const assessmentUrl = selectedForm?.slug
    ? `${window.location.origin}${config.intakePath}/${selectedForm.slug}`
    : null;

  const copyUrl = async () => {
    if (!assessmentUrl) return;
    try {
      await navigator.clipboard.writeText(assessmentUrl);
      notify.success("Link copied to clipboard");
    } catch (error) {
      notify.error("Could not copy the link");
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.includes(q)
    );
  }, [users, searchQuery]);

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedUserIds(new Set(filteredUsers.map((u) => u.id)));
  const deselectAll = () => setSelectedUserIds(new Set());

  const handleSend = async () => {
    if (selectedUserIds.size === 0 || !selectedFormId) return;

    try {
      setSending(true);
      const response = await service.sendAssessment(
        selectedFormId,
        Array.from(selectedUserIds),
        deliveryMethod
      );
      if (response.success) {
        const { sentCount, skippedCount, failedCount } = response.data;
        if (sentCount > 0) {
          track(config.events.assessmentSent);
          notify.success(`Assessment sent to ${people(sentCount)}`);
        }
        if (skippedCount > 0) {
          notify.info(
            `${people(skippedCount)} skipped (already sent or missing contact info)`
          );
        }
        if (failedCount > 0) {
          notify.error(`Could not send to ${people(failedCount)}`);
        }
        onClose();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not send the assessment"));
    } finally {
      setSending(false);
    }
  };

  const renderUsers = () => {
    if (loadingUsers) {
      return (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-control" />
          ))}
        </div>
      );
    }
    if (users.length === 0) {
      return (
        <EmptyState
          icon={MdPeople}
          title={`No Eligible ${labels.plural}`}
          description={`Your ${labels.pluralLower} may have already completed this assessment.`}
        />
      );
    }
    if (filteredUsers.length === 0) {
      return <EmptyState variant="filtered" title="No people match your search" />;
    }
    return (
      <ul className="max-h-72 overflow-y-auto rounded-control border border-line">
        {filteredUsers.map((user) => (
          <li key={user.id} className="border-b border-line last:border-b-0">
            <label className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-subtle">
              <Checkbox
                checked={selectedUserIds.has(user.id)}
                onChange={() => toggleUser(user.id)}
              />
              <Avatar user={user} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-fg">
                  {user.firstName} {user.lastName}
                </p>
                <p className="truncate text-xs text-fg-muted">
                  {user.email}
                  {user.phone ? ` · ${user.phone}` : ""}
                </p>
              </div>
              {user.role && (
                <Badge tone="neutral" size="sm" className="capitalize">
                  {user.role}
                </Badge>
              )}
            </label>
          </li>
        ))}
      </ul>
    );
  };

  const renderBody = () => {
    if (loadingForms) return <Loader size="sm" label="Loading assessment forms" />;
    if (forms.length === 0) {
      return (
        <EmptyState
          icon={MdAssignment}
          title="No Active Assessment Forms"
          description="Create an assessment form in Settings first."
          action={
            <Button as={Link} to={`${config.basePath}/settings`} variant="secondary">
              Go to Settings
            </Button>
          }
        />
      );
    }
    return (
      <div className="flex flex-col gap-4">
        {forms.length > 1 && (
          <Select
            label="Assessment Form"
            value={selectedFormId ?? ""}
            onChange={(e) => setSelectedFormId(Number(e.target.value))}
            options={forms.map((f) => ({ value: f.id, label: f.name }))}
          />
        )}

        {assessmentUrl && (
          <div className="flex items-end gap-2">
            <InputField
              label="Assessment Link"
              value={assessmentUrl}
              readOnly
              onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1"
            />
            <IconButton
              icon={<MdContentCopy />}
              label="Copy Link"
              variant="secondary"
              onClick={copyUrl}
            />
          </div>
        )}

        <div>
          <p className="mb-1.5 text-sm font-medium text-fg">Delivery Method</p>
          <SegmentedControl
            aria-label="Delivery Method"
            options={DELIVERY_OPTIONS}
            value={deliveryMethod}
            onChange={setDeliveryMethod}
          />
        </div>

        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name, email, or phone…"
          ariaLabel="Search eligible people"
        />

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-fg-muted">
            {filteredUsers.length} eligible {filteredUsers.length === 1 ? "person" : "people"}
          </span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={selectAll}>
              Select All
            </Button>
            <Button variant="ghost" size="sm" onClick={deselectAll}>
              Deselect All
            </Button>
          </div>
        </div>

        {renderUsers()}
      </div>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Send Assessment"
      description={`Invite ${labels.pluralLower} to take the ministry archetype assessment.`}
      footer={
        <>
          <span className="mr-auto text-sm text-fg-muted">
            {selectedUserIds.size} selected
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftIcon={<MdSend />}
            loading={sending}
            disabled={selectedUserIds.size === 0 || !selectedFormId}
            onClick={handleSend}
          >
            Send Assessment
          </Button>
        </>
      }
    >
      {renderBody()}
    </Dialog>
  );
};

export default SendAssessmentModal;
